/**
 * Below-the-fold sections of the marketing page are lazy chunks, mounted
 * when they come near the viewport (VerticalScroller's DeferredSection). Code
 * that jumps to a section by its `data-site-section` key must therefore cope
 * with a section that isn't in the DOM yet: `scrollToSiteSection` asks every
 * deferred section to mount, waits until they all rendered (so the target
 * doesn't move under the scroll as the ones above it grow from their
 * placeholders), then scrolls.
 */

/**
 * In-view trigger for an entrance animation that wraps a whole panel. A
 * fractional `amount` (e.g. 0.2) never fires once the panel is taller than
 * the viewport divided by it — on a phone the Countries panel is ~4300 px, so
 * 20% of it never fits on screen and the section stayed at opacity 0. This
 * fires once the panel's top is 15% into the viewport, whatever its height.
 */
export const PANEL_IN_VIEW = { once: true, amount: "some", margin: "0px 0px -15% 0px" } as const;

/** Window event: mount every deferred section now. */
export const MOUNT_ALL_SECTIONS_EVENT = "site:mount-all-sections";
/** Window event: a deferred section finished rendering (scroll-spy re-wires). */
export const SECTION_RENDERED_EVENT = "site:section-rendered";

/** Deferred sections not rendered yet — maintained by DeferredSection. */
let pending = 0;

export const sectionRegistry = {
    add(): void { pending += 1; },
    rendered(): void {
        pending = Math.max(0, pending - 1);
        if (typeof window !== "undefined") window.dispatchEvent(new Event(SECTION_RENDERED_EVENT));
    },
};

export const findSiteSection = (key: string): HTMLElement | null =>
    typeof document === "undefined" ? null : document.querySelector<HTMLElement>(`[data-site-section='${key}']`);

/** Mount every deferred section (e.g. before a print or a deep link). */
export const mountAllSections = (): void => {
    if (typeof window !== "undefined") window.dispatchEvent(new Event(MOUNT_ALL_SECTIONS_EVENT));
};

/**
 * Smooth-scrolls to the section with this `data-site-section` key, mounting
 * the deferred ones first when needed. Gives up quietly after a few seconds
 * (a chunk that never loads must not leave a loop running).
 */
export const scrollToSiteSection = (key: string, block: ScrollLogicalPosition = "start"): void => {
    const target = findSiteSection(key);
    if (target && pending === 0) {
        target.scrollIntoView({ behavior: "smooth", block });
        return;
    }
    mountAllSections();
    const started = typeof performance !== "undefined" ? performance.now() : Date.now();
    const tick = () => {
        const el = findSiteSection(key);
        if (el && pending === 0) {
            // One more frame so the freshly rendered sections have laid out.
            requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block }));
            return;
        }
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        if (now - started < 5000) {
            requestAnimationFrame(tick);
        } else if (el) {
            el.scrollIntoView({ behavior: "smooth", block });
        }
    };
    requestAnimationFrame(tick);
};
